import React, { PureComponent } from 'react';
import styled from 'styled-components';

import { getRequest } from '../../api/ApiService';
import { changeLocation } from '../../api/Location';
import Auth from '../../routes/Auth';
import EventEmitter from '../../services/event-emitter';
import { clearAppGeneralInfoCache } from '../../api/AppGeneralInfoService';

// End-of-round processing lock screen. Replaces the previous SweetAlert
// "Currently processing round. Please be patient." warning.
//
// Trigger: mounted whenever App.js's isRoundProcessing is true. Once
// mounted, the popup owns its own visibility via state.active so a
// mid-flight fetchData that flips isRoundProcessing back to false
// doesn't rip it away before the student clicks View Results.
//
// Progress:
//   Polls /api/game/rounds/<round_id>/end-round-status/ on a decaying
//   schedule (10s x3, 15s x2, 30s xN up to 5min, then stop). Response
//   has 7 backend steps which we group into 3 UI stages:
//     1. Closing the Round          -> before_end_round
//     2. Processing Sales           -> allocate_round
//     3. Generating Financial Reports -> end_round_reports, grade_round,
//                                        performance_feedback,
//                                        engagement_scoring,
//                                        after_end_round
//   404 ("This round has not been processed yet.") is treated as "all
//   three stages pending" — keep polling.
//
// View Results click mirrors StudentResultsNotifications.viewResults:
// PATCH course_members/{id}/viewed with rd{N}_viewed: true, then
// changeLocation to the results-are-in flow.

const STAGE_MAP = [
  { title: 'Closing the Round', steps: ['before_end_round'] },
  { title: 'Processing Sales', steps: ['allocate_round'] },
  {
    title: 'Generating Financial Reports',
    steps: [
      'end_round_reports',
      'grade_round',
      'performance_feedback',
      'engagement_scoring',
      'after_end_round',
    ],
  },
];

const POLL_MAX_MS = 300000;
const scheduleForElapsed = (elapsedMs) => {
  if (elapsedMs >= POLL_MAX_MS) return null;
  if (elapsedMs < 30000) return 10000;
  if (elapsedMs < 60000) return 15000;
  return 30000;
};

// Detects a failure signal in the status response. Any of these count:
//   - overall job status is 'failed' / 'error'
//   - top-level error_message is non-empty
//   - any individual step is 'failed' / 'error'
// When triggered we silently freeze the popup at its current progress
// per product decision — never surface backend failures to students.
const hasFailure = (body) => {
  if (!body) return false;
  const jobStatus = String(body.status || '').toLowerCase();
  if (jobStatus === 'failed' || jobStatus === 'error') return true;
  if (body.error_message) return true;
  const steps = Array.isArray(body.steps) ? body.steps : [];
  return steps.some((s) => {
    const st = String((s && s.status) || '').toLowerCase();
    return st === 'failed' || st === 'error';
  });
};

// Group the flat BE step array into a 3-stage view. Each stage:
//   status: 'done'    -> every constituent step is done
//   status: 'running' -> at least one step is running, or any is done
//                        without all being done (partial progress)
//   status: 'pending' -> every step pending / missing
const groupSteps = (steps) => {
  const byName = {};
  (steps || []).forEach((s) => {
    if (s && s.name) byName[s.name] = s.status;
  });
  return STAGE_MAP.map((stage) => {
    const statuses = stage.steps.map((n) => byName[n] || 'pending');
    const allDone = statuses.every((s) => s === 'done');
    const anyRunning = statuses.some((s) => s === 'running');
    const anyDone = statuses.some((s) => s === 'done');
    let status;
    if (allDone) status = 'done';
    else if (anyRunning || anyDone) status = 'running';
    else status = 'pending';
    return { title: stage.title, status };
  });
};

const FONT = "'Mukta Vaani', sans-serif";

const Backdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 560px;
  padding: 36px 40px;
  text-align: center;
  font-family: ${FONT};
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.72);
  margin: 0 0 28px;
`;

const StepperRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin: 0 auto 12px;
  max-width: 480px;
`;

// Each stage renders as a rounded segment. Fills teal when done, shows
// a soft striped/pulsing fill while running, stays grey when pending.
const stripeAnimation = 'progressStripe 1.2s linear infinite';
const Segment = styled.div`
  flex: 1;
  height: 12px;
  border-radius: 999px;
  background: ${p =>
    p.status === 'done'
      ? '#52C5CD'
      : p.status === 'running'
        ? 'repeating-linear-gradient(45deg, #52C5CD, #52C5CD 8px, #B8E5E9 8px, #B8E5E9 16px)'
        : '#E5E9F0'};
  transition: background 200ms ease;
  animation: ${p => (p.status === 'running' ? stripeAnimation : 'none')};

  @keyframes progressStripe {
    from { background-position: 0 0; }
    to   { background-position: 32px 0; }
  }
`;

const StageLabels = styled.div`
  display: flex;
  gap: 8px;
  max-width: 480px;
  margin: 0 auto 28px;
`;

const StageLabel = styled.div`
  flex: 1;
  font-size: 12px;
  line-height: 16px;
  color: ${p => (p.status === 'pending' ? 'rgba(0, 27, 59, 0.4)' : 'rgba(0, 27, 59, 0.7)')};
  font-weight: ${p => (p.status === 'done' ? 600 : 500)};
  text-align: center;
`;

// Escape hatch for students who don't want to sit through the wait.
// The popup dismisses itself once processing completes (see auto-
// dismiss in componentDidUpdate) so there's no primary action button
// here — StudentResultsNotifications takes over with the "Round N
// Results Ready to Review" popup and its View Results button.
const LogoutLink = styled.button`
  display: block;
  margin: 8px auto 0;
  padding: 6px 10px;
  background: transparent;
  border: none;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.55);
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: rgba(0, 27, 59, 0.85); }
`;

class RoundProcessingPopup extends PureComponent {
  state = {
    // Owns visibility once mounted so an isRoundProcessing race can't
    // rip the popup away mid-flight. Auto-dismisses when the two-source
    // done check clears (overall_percent === 100 AND round.is_finished
    // === true) — at that point StudentResultsNotifications fires with
    // its own "Round N Results Ready to Review" popup.
    active: !!this.props.isRoundProcessing,
    stages: STAGE_MAP.map((s) => ({ title: s.title, status: 'pending' })),
    overallPercent: 0,
    jobStatus: null, // 'running' | 'done' | 'error' | null
  };

  pollTimer = null;
  dismissTimer = null;
  dismissAttempts = 0;
  startedAt = 0;
  isMounted_ = false;

  componentDidMount() {
    this.isMounted_ = true;
    if (this.state.active) {
      this.beginPolling();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isRoundProcessing && !prevProps.isRoundProcessing) {
      this.setState({ active: true }, () => {
        if (!this.pollTimer && this.startedAt === 0) this.beginPolling();
      });
    }
    // Auto-dismiss the moment the two-source done gate flips true.
    // Uses componentDidUpdate so we catch both paths that get us here:
    //   - polling response updates state.overallPercent to 100
    //   - App.js refetches general-info and props.round flips
    //     is_finished to true
    if (this.state.active && this.isJobDone()) {
      this.clearPollTimer();
      this.clearDismissTimer();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ active: false });
    }
  }

  componentWillUnmount() {
    this.isMounted_ = false;
    this.clearPollTimer();
    this.clearDismissTimer();
  }

  clearPollTimer = () => {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  };

  clearDismissTimer = () => {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  };

  beginPolling = () => {
    this.startedAt = Date.now();
    // Small initial delay before the first status call. Guards against
    // an instructor re-closing the round: at re-close moment the BE can
    // briefly still return the previous run's 'succeeded'/100% state,
    // which would trip our done detection and dismiss the popup on
    // stale data. The popup still renders immediately with all-pending
    // stages so students see the progress bar during the wait.
    this.pollTimer = setTimeout(this.fetchStatus, 2000);
  };

  scheduleNextPoll = () => {
    const elapsed = Date.now() - this.startedAt;
    const nextDelay = scheduleForElapsed(elapsed);
    if (nextDelay == null) return; // exceeded 5min, stop
    this.clearPollTimer();
    this.pollTimer = setTimeout(this.fetchStatus, nextDelay);
  };

  fetchStatus = () => {
    const { round } = this.props;
    const roundId = round && round.id;
    if (!roundId) {
      // No round id to poll against. Keep re-checking in case props
      // populate late.
      this.scheduleNextPoll();
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    getRequest(`${apiUrl}game/rounds/${roundId}/end-round-status/`)
      .then((res) => {
        if (!this.isMounted_) return;
        const body = (res && res.body) || {};
        // Silent-freeze on backend failure: don't apply the response's
        // stages/percent (which would show a regression or expose an
        // error state), and don't schedule another poll. The popup
        // holds whatever progress the previous successful poll rendered,
        // and the View Results button stays disabled — the student
        // never learns processing died on the server side, they just
        // see the bar stuck partway.
        if (hasFailure(body)) return;
        const percent = Number(body.overall_percent) || 0;
        this.setState({
          stages: groupSteps(body.steps),
          overallPercent: percent,
          jobStatus: body.status || null,
        });
        // Treat any of these as "processing complete":
        //   - status: 'done'       (documented)
        //   - status: 'succeeded'  (what the BE actually returns on
        //                           successful completion)
        //   - overall_percent >= 100 (belt-and-suspenders — if the
        //     status string drifts again the percent still stops us)
        const jobStatusStr = String(body.status || '').toLowerCase();
        const jobDone =
          jobStatusStr === 'done'
          || jobStatusStr === 'succeeded'
          || percent >= 100;
        if (!jobDone) {
          this.scheduleNextPoll();
        } else {
          // Stop status polling, then defer the general-info refetch
          // until end_date has passed by a few seconds — otherwise BE
          // is likely to still return is_finished: false and the
          // two-source dismissal gate keeps the popup stuck.
          this.scheduleDismissWhenReady();
        }
      })
      .catch((err) => {
        if (!this.isMounted_) return;
        // 404 = "This round has not been processed yet." — treat as
        // "queued, all stages pending" and keep polling. Any other error
        // also just schedules the next poll; we don't want to fail
        // permanently on a transient blip. scheduleNextPoll enforces
        // the 300s ceiling for 404s and errors just like it does for
        // success responses — nothing here gets to poll forever.
        this.scheduleNextPoll();
      });
  };

  isJobDone = () => {
    // Two-source gate — dismiss the popup only when the status endpoint
    // says 100% AND App.js has already seen the round flip to
    // is_finished=true. We delay the reload-data call after status
    // reports done (see scheduleDismissWhenReady) so that by the time
    // general-info is refetched, BE has committed is_finished. That
    // lets this strict check stay in place and prevents dismissing
    // before StudentResultsNotifications is ready to take over (its
    // isRoundEnded needs end_date < now).
    const { round } = this.props;
    const { overallPercent } = this.state;
    return overallPercent === 100 && round && round.is_finished === true;
  };

  // Called once when the status endpoint first reports done. Runs a
  // retry ladder that refetches general-info at end_date + 10s, then
  // again 10s later, then 10s later — 3 attempts, ~30s total. BE has
  // been observed committing is_finished 5-10s past end_date on the
  // round record, so a single refetch was landing before BE caught up.
  // If is_finished flips during any attempt, componentDidUpdate's
  // two-source gate dismisses the popup and clearDismissTimer cancels
  // the remaining attempts. After 3 tries we give up and let the next
  // natural fetchData tick catch it — never poll forever.
  scheduleDismissWhenReady = () => {
    if (this.dismissTimer || this.dismissAttempts > 0) return;
    const { round } = this.props;
    const endDate = round && round.end_date
      ? new Date(round.end_date).getTime()
      : Date.now();
    const scheduleAttempt = (delay) => {
      this.dismissTimer = setTimeout(() => {
        this.dismissTimer = null;
        if (!this.isMounted_ || !this.state.active) return;
        this.dismissAttempts += 1;
        clearAppGeneralInfoCache();
        EventEmitter.getInstance().trigger('reload-data');
        if (this.dismissAttempts < 3) {
          scheduleAttempt(10000);
        }
      }, delay);
    };
    const initialDelay = Math.max(0, endDate + 10000 - Date.now());
    scheduleAttempt(initialDelay);
  };

  handleLogout = () => {
    if (Auth.isUserAuthenticated()) {
      Auth.deauthenticateUser();
    }
    changeLocation('/login');
  };

  render() {
    if (!this.state.active) return null;
    const { stages } = this.state;

    return (
      <Backdrop>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>Round Results are processing.</Title>
          <Subtitle>It can take up to 5 minutes.</Subtitle>
          <StepperRow>
            {stages.map((s) => (
              <Segment key={s.title} status={s.status} />
            ))}
          </StepperRow>
          <StageLabels>
            {stages.map((s) => (
              <StageLabel key={s.title} status={s.status}>
                {s.title}
              </StageLabel>
            ))}
          </StageLabels>
          <LogoutLink type="button" onClick={this.handleLogout}>
            Logout
          </LogoutLink>
        </Card>
      </Backdrop>
    );
  }
}

export default RoundProcessingPopup;



// WEBPACK FOOTER //
// ./src/components/round-processing/index.js